// @flow
import * as React from 'react';
import { OrderedMap } from 'immutable';
//$FlowFixMe
import { marked } from 'marked';
import { Alert, HelpDrawer } from '@cmsgov/ds-healthcare-gov';
import IncomeContainer from './IncomeContainer';
import generateId from '../../utilities/generateId';
import ExpenseContainer from './ExpenseContainer';
import HouseholdMemberInfo from './HouseholdMemberInfo';
import NetExpectedIncome from './NetExpectedIncome';
import NextSteps from './NextSteps';
import buildPdf from '../../utilities/buildPdf';
import { sendEvent, getEnvironment } from '../../utilities/analytics';

import type { FrequencyType } from '../../common/enums/FrequencyTypes';
import type { IncomeType } from '../../common/enums/IncomeTypes';
import type { TFunction } from 'react-i18next';
import type { DateObject } from '@cmsgov/ds-healthcare-gov';

export type Income = {
  amount: string,
  calculatedTotal: number,
  incomeFrequency: FrequencyType,
  incomeType: IncomeType,
  startDate: DateObject,
  endDate: DateObject,
  id?: string,
};

export type Expense = {
  expenseType: string,
  amount: string,
  expenseFrequency: FrequencyType,
  calculatedTotal: number,
};

type Props = {
  // TODO: with hooks, we could use useTranslation
  t: TFunction,
};

type State = {
  name: ?string,
  incomeTotal: number,
  expensesTotal: number,
  incomes: OrderedMap<string, Income>,
  expenses: OrderedMap<string, Expense>,
  helpDrawer: { active: boolean, heading: string, body: string, id: ?string },
  key: number,
};

const initialHelpDrawer = {
  active: false,
  heading: '',
  body: '',
  id: null,
};

class IncomeCalculator extends React.Component<Props, State> {
  initialState = {
    name: null,
    incomeTotal: 0,
    expensesTotal: 0,
    incomes: OrderedMap(),
    expenses: OrderedMap(),
    helpDrawer: initialHelpDrawer,
    key: 0,
  };

  state = this.initialState;

  componentDidMount() {
    if (window.utag_data) {
      window.utag_data.page_name = 'app 3.0: estimate income';
      window.utag_data.content_language = window.location.hostname.includes('cuidadodesalud.gov') ? 'es' : 'en';
      window.utag_data.content_type = 'estimate income page';
      window.utag_data.site_division = 'apply';
      window.utag_data.site_domain = window.location.hostname;
      window.utag_data.site_environment = getEnvironment();
      window.utag_data.site_section = 'application - unauthenticated tool';
      window.utag_data.page_type = 'tool';
      window.utag_data.page_topic = 'income';
      window.utag_data.page_lifecycle = 'education';
      window.utag_data.page_audience = 'individual';
      window.utag_data.page_category = 'income-and-household-information';
    }
  }

  showHelpDrawer = ({ heading, body, id }: { heading: string, body: string, id: string }) => {
    this.setState({ helpDrawer: { active: true, heading, body, id } });
  };

  closeHelpDrawer = () => {
    this.setState({ helpDrawer: initialHelpDrawer });
  };

  // Member Name
  handleUpdateHouseholdMemberName = (name: string) => {
    this.setState({ name });
  };

  // Income
  handleAddNewIncome = (incomeRowState: Income) => {
    const id = generateId('income');
    this.setState({ incomes: this.state.incomes.set(id, { ...incomeRowState, id }) }, () => {
      this.updateTotal();
    });
  };

  handleRemoveIncome = (id: string) => {
    this.setState({ incomes: this.state.incomes.delete(id) }, () => this.updateTotal());
  };

  handleUpdateIncomeEntry = (incomeState: Income, id: string) => {
    this.setState({ incomes: this.state.incomes.set(id, { ...incomeState, id }) }, () => this.updateTotal());
  };

  updateTotal = () => {
    const incomeTotal = this.state.incomes.reduce((accum, current) => {
      return parseFloat(accum) + parseFloat(current.calculatedTotal);
    }, 0);
    this.setState({ incomeTotal });
  };

  // Expenses
  handleAddNewExpense = (expenseRowState: Expense) => {
    const id = generateId('expense');
    this.handleUpdateExpenseEntry(expenseRowState, id);
  };

  handleRemoveExpense = (id: string) => {
    this.setState({ expenses: this.state.expenses.delete(id) }, () => this.updateExpenseTotal());
  };

  handleUpdateExpenseEntry = (expenseState: Expense, id: string) => {
    this.setState({ expenses: this.state.expenses.set(id, { ...expenseState }) }, () => this.updateExpenseTotal());
  };

  updateExpenseTotal = () => {
    const expensesTotal = this.state.expenses.reduce((accum, current) => {
      return parseFloat(accum) + parseFloat(current.calculatedTotal);
    }, 0);
    this.setState({ expensesTotal });
  };

  // Clear form
  handleClearForm = () => {
    const key = this.state.key;
    sendEvent('estimate income', 'cleared form', 'unauthenticated', 'clear_form');
    this.setState({ ...this.initialState, key: key + 1 });
  };

  handleBuildPDF = () => {
    const { name, incomeTotal, expensesTotal, incomes, expenses } = this.state;
    sendEvent('download', 'pdf', 'income-estimate.pdf', 'file_download');
    buildPdf(this.props.t, name, incomeTotal, expensesTotal, incomes, expenses);
  };

  render() {
    return (
      <main id="main-body-content" className="ds-l-container ds-u-margin-bottom--4" tabIndex="-1" role="main">
        {this.state.helpDrawer.active && (
          <HelpDrawer
            heading={this.state.helpDrawer.heading}
            onCloseClick={this.closeHelpDrawer}
            closeButtonText={this.props.t('close')}
            footerTitle={this.props.t('helpDrawerFooterTitle')}
            footerBody={
              <div dangerouslySetInnerHTML={{ __html: marked.parse(this.props.t('helpDrawerFooterBody')) }} />
            }
          >
            <div dangerouslySetInnerHTML={{ __html: marked.parse(this.state.helpDrawer.body) }} />
          </HelpDrawer>
        )}
        <h1 className="ds-title ds-u-margin-top--4">{this.props.t('estimatorPageTitle')}</h1>
        <div className="ds-u-font-size--h3 ds-u-margin-top--2 ds-u-margin-bottom--1">
          {this.props.t('estimatorLead')}
        </div>
        <Alert
          className="ds-u-margin-top--3 ds-u-margin-bottom--2"
          heading={this.props.t('estimatorAlertTitle')}
          children={this.props.t('estimatorAlertBody')}
        />
        <HouseholdMemberInfo name={this.state.name} updateHouseholdMemberName={this.handleUpdateHouseholdMemberName} />
        <IncomeContainer
          incomes={this.state.incomes}
          incomeTotal={this.state.incomeTotal}
          onAddNewIncome={this.handleAddNewIncome}
          handleUpdateIncomeEntry={this.handleUpdateIncomeEntry}
          handleRemoveIncomeEntry={this.handleRemoveIncome}
          activeHelpDrawer={this.state.helpDrawer.id}
          showHelpDrawer={this.showHelpDrawer}
          key={`income-${this.state.key}`}
        />
        <ExpenseContainer
          onAddNewExpense={this.handleAddNewExpense}
          expensesTotal={this.state.expensesTotal}
          expenses={this.state.expenses}
          handleUpdateExpenseEntry={this.handleUpdateExpenseEntry}
          handleRemoveExpenseEntry={this.handleRemoveExpense}
          key={`expense-${this.state.key}`}
        />
        <NetExpectedIncome netTotal={this.state.incomeTotal - this.state.expensesTotal} />
        <NextSteps clearForm={this.handleClearForm} buildPDF={this.handleBuildPDF} />
      </main>
    );
  }
}

export default IncomeCalculator;
